import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

const POP_WIDTH = 250;
const POP_MAX_HEIGHT = 140;
const MARGIN = 12;

// Small tap-to-open "i" explainer bubble — mobile-friendly, portaled + viewport-clamped
// so it always sits fully on-screen and above every other element.
export function InfoTip({ text, testId }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(Math.max(MARGIN, r.left), window.innerWidth - POP_WIDTH - MARGIN);
    const fitsBelow = r.bottom + 6 + POP_MAX_HEIGHT <= window.innerHeight - MARGIN;
    const top = fitsBelow ? r.bottom + 6 : Math.max(MARGIN, r.top - 6 - POP_MAX_HEIGHT);
    setPos({ top, left, flipped: !fitsBelow });
  };

  useEffect(() => {
    if (!open) return;
    const onOutside = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (e.target.closest && e.target.closest('.info-tip-pop')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('touchstart', onOutside);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('touchstart', onOutside);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) place();
    setOpen(v => !v);
  };

  return (
    <span className="info-tip">
      <button
        type="button"
        ref={btnRef}
        className="info-tip-btn"
        data-testid={testId}
        aria-label="More info"
        onClick={toggle}
      >
        <Info size={12} />
      </button>
      {open && pos && createPortal(
        <div
          className="info-tip-pop"
          data-testid={testId ? `${testId}-pop` : undefined}
          style={{ top: pos.top, left: pos.left, width: POP_WIDTH, maxHeight: POP_MAX_HEIGHT, overflowY: 'auto' }}
        >
          {text}
        </div>,
        document.body
      )}
    </span>
  );
}
